// src/components/SubscriptionInfo.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { Crown, CheckCircle } from 'lucide-react';
import styles from './SubscriptionInfo.module.css';
import { httpsCallable } from 'firebase/functions';

const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/dR614x7iH0E44366oo";

const SubscriptionInfo = ({ userId }) => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      try {
        const userDocRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Calculate subscription details based on all possible fields
          let subscriptionInfo = {
            status: 'free',
            tier: 'free',
            startDate: null,
            endDate: null
          };
          
          // Flag to track if user has an active subscription
          let hasActiveSubscription = false;
          
          // Check subscriptionEndTimestamp (could be a string or a Firestore timestamp)
          if (userData.subscriptionEndTimestamp) {
            let endDate;
            
            // Handle string format
            if (typeof userData.subscriptionEndTimestamp === 'string') {
              endDate = new Date(userData.subscriptionEndTimestamp);
            } 
            // Handle Firestore timestamp format
            else if (userData.subscriptionEndTimestamp.toDate) {
              endDate = userData.subscriptionEndTimestamp.toDate();
            }
            
            // If we have a valid date and it's in the future, user is still Pro
            if (endDate && !isNaN(endDate) && endDate > new Date()) {
              console.log('Active subscription found via subscriptionEndTimestamp - expires:', endDate);
              hasActiveSubscription = true;
              subscriptionInfo.endDate = endDate;
            }
          }
          
          // Also check proEndDate as a fallback - proEndDate is always a string
          if (!hasActiveSubscription && userData.proEndDate) {
            // proEndDate should already be a string, so we just need to convert it to a Date object
            const endDate = new Date(userData.proEndDate);
            
            // If we have a valid date and it's in the future, user is still Pro
            if (!isNaN(endDate) && endDate > new Date()) {
              console.log('Active subscription found via proEndDate - expires:', endDate);
              hasActiveSubscription = true;
              subscriptionInfo.endDate = endDate;
            }
          }
          
          // Get start date (if available) - proStartDate is always a string
          if (userData.proStartDate) {
            // proStartDate should already be a string, so we just convert to Date
            const startDate = new Date(userData.proStartDate);
            
            if (!isNaN(startDate)) {
              subscriptionInfo.startDate = startDate;
            }
          }
          
          // Set tier based on active subscription or explicit tierType
          if (hasActiveSubscription || userData.tierType === 'pro') {
            subscriptionInfo.status = 'active';
            subscriptionInfo.tier = 'pro';
            
            // Only calculate next billing date if no end date and not canceled
            if (subscriptionInfo.startDate && !subscriptionInfo.endDate && 
                userData.subscriptionStatus !== 'canceled') {
              
              const startDate = new Date(subscriptionInfo.startDate);
              const nextBillingDate = new Date(startDate);
              
              // Assuming monthly billing cycle
              nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
              
              // If we've passed a billing date, find the next one
              while (nextBillingDate < new Date()) {
                nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
              }
              
              subscriptionInfo.nextBillingDate = nextBillingDate;
              // Store subscription state to show appropriate message
              subscriptionInfo.subscriptionStatus = 'active';
            } 
            // For canceled subscriptions with future end date
            else if (subscriptionInfo.endDate && userData.subscriptionStatus === 'canceled') {
              subscriptionInfo.subscriptionStatus = 'canceled';
            }
          }
          
          console.log('Subscription info:', subscriptionInfo);
          setSubscriptionData(subscriptionInfo);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        setLoading(false);
      }
    };
    
    fetchSubscriptionData();
  }, [userId]);

  const handleUpgrade = () => {
    // Redirect to Stripe payment link
    window.location.href = STRIPE_PAYMENT_LINK;
  };

  const handleManageSubscription = () => {
    navigate('/dashboard', { state: { activeSection: 'cancelAccount' } });
  };

  const handleCancelSubscription = async () => {
    try {
      const cancelSubscription = httpsCallable(functions, 'cancelSubscription');
      await cancelSubscription();
      
      // Refresh subscription data after cancellation
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Determine end date from either field
        let endDate = null;
        if (userData.subscriptionEndTimestamp) {
          if (typeof userData.subscriptionEndTimestamp === 'string') {
            endDate = new Date(userData.subscriptionEndTimestamp);
          } else if (userData.subscriptionEndTimestamp.toDate) {
            endDate = userData.subscriptionEndTimestamp.toDate();
          }
        } else if (userData.proEndDate) {
          if (typeof userData.proEndDate === 'string') {
            endDate = new Date(userData.proEndDate);
          } else if (userData.proEndDate.toDate) {
            endDate = userData.proEndDate.toDate();
          }
        }
        
        setSubscriptionData({
          status: 'active', // Still active until end date
          tier: 'pro',     // Still pro until end date
          subscriptionStatus: 'canceled', // But marked as canceled
          endDate: endDate
        });
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date instanceof Date 
      ? date.toLocaleDateString(undefined, options)
      : new Date(date).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className={styles.subscriptionContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading subscription information...</p>
      </div>
    );
  }

  return (
    <div className={styles.subscriptionContainer}>
      
      {/* Subscription Status Card */}
      <div className={`${styles.statusCard} ${styles[subscriptionData?.tier || 'free']}`}>
        <div className={styles.statusHeader}>
          {subscriptionData?.tier === 'pro' ? (
            <>
              <Crown size={24} className={styles.statusIcon} />
              <h3>Pro Subscription</h3>
            </>
          ) : (
            <>
              <CheckCircle size={24} className={styles.statusIcon} />
              <h3>Free Account</h3>
            </>
          )}
        </div>
        
        <div className={styles.statusBody}>
          {subscriptionData?.tier === 'pro' ? (
            <>
              <div className={styles.statusItem}>
                <span className={styles.statusLabel}>Status:</span>
                <span className={styles.statusValue}>
                  {subscriptionData.subscriptionStatus === 'canceled' 
                    ? 'Canceled (Pro until expiry)' 
                    : 'Active'}
                </span>
              </div>
              
              {subscriptionData?.startDate && (
                <div className={styles.statusItem}>
                  <span className={styles.statusLabel}>Started On:</span>
                  <span className={styles.statusValue}>{formatDate(subscriptionData.startDate)}</span>
                </div>
              )}
              
              {subscriptionData?.nextBillingDate && subscriptionData.subscriptionStatus !== 'canceled' && (
                <div className={styles.statusItem}>
                  <span className={styles.statusLabel}>Next Payment:</span>
                  <span className={styles.statusValue}>{formatDate(subscriptionData.nextBillingDate)}</span>
                </div>
              )}
              
              {subscriptionData?.endDate && (
                <div className={styles.statusItem}>
                  <span className={styles.statusLabel}>
                    {subscriptionData.subscriptionStatus === 'canceled' 
                      ? 'Pro Access Until' 
                      : 'Current Period Ends'}:
                  </span>
                  <span className={styles.statusValue}>{formatDate(subscriptionData.endDate)}</span>
                </div>
              )}
              
              <div className={styles.proFeatureList}>
                <h4>Pro Features:</h4>
                <ul>
                  <li>Up to 30 links and 25 icons</li>
                  <li>Ghost mode for privacy and profile security</li>
                  <li>Link ordering, hidden links, pinned links and ghost links</li>
                  <li>All themes</li>
                  <li>All Palettes</li>
                  <li>Link shortener with history</li>
                  <li>Link analytics</li>
                  <li>Link and profile QR codes</li>
                  <li>Full access to all new features</li>
                </ul>
              </div>
            </>
          ) : (
            <>
              <div className={styles.statusItem}>
                <span className={styles.statusLabel}>Status:</span>
                <span className={styles.statusValue}>Active</span>
              </div>
              
              <div className={styles.freeFeatureList}>
                <h4>Free Features:</h4>
                <ul>
                  <li>Up to 5 links and 5 icons</li>
                  <li>Profile picture</li>
                  <li>Themes</li>
                  <li>Profile QR code</li>
                  <li>Bio and banner</li>
                  <li>32 reversable palattes covering over 60 palatte combinations</li>
                </ul>
              </div>
              
              <div className={styles.upgradeInfo}>
                <h4>Upgrade to Pro:</h4>
                <p>Get access to all premium features for €3/month.</p>
              </div>
            </>
          )}
        </div>
        
        <div className={styles.statusFooter}>
          {subscriptionData?.tier === 'pro' ? (
            <>
              {subscriptionData.subscriptionStatus !== 'canceled' ? (
                <>
                  <button className={styles.manageButton} onClick={handleManageSubscription}>
                    Manage
                  </button>
                  <button className={styles.cancelButton} onClick={handleCancelSubscription}>
                    Cancel
                  </button>
                </>
              ) : (
                <div className={styles.canceledMessage}>
                  <p>Your subscription has been canceled but you'll keep Pro access until the expiry date.</p>
                </div>
              )}
            </>
          ) : (
            <button className={styles.upgradeButton} onClick={handleUpgrade}>
              <Crown size={16} /> Upgrade to Pro
            </button>
          )}
        </div>
      </div>
      
      {/* FAQ Section */}
      <div className={styles.faqSection}>
        <h3>Subscription FAQ</h3>
        
        <div className={styles.faqItem}>
          <h4>How do I cancel my subscription?</h4>
          <p>You can cancel your Pro subscription at any time. Go to "Manage Subscription" and select "Cancel Subscription". Your Pro features will remain active until the end of your current billing period.</p>
        </div>
        
        <div className={styles.faqItem}>
          <h4>When will I be charged?</h4>
          <p>Your subscription fee is charged monthly on the same date you initially subscribed. For example, if you subscribed on the 15th, you'll be charged on the 15th of each month.</p>
        </div>
        
        <div className={styles.faqItem}>
          <h4>What happens to my Pro features if I cancel?</h4>
          <p>After cancelling, you'll keep all Pro features until the end of your current billing period. After that, if you have more than 5 links, the extra links will be hidden but not deleted. You'll lose access to Pro-only features like ghost mode and link ordering.</p>
        </div>
        
        <div className={styles.faqItem}>
          <h4>Can I subscribe again after cancelling?</h4>
          <p>Yes, you can upgrade back to Pro at any time. Your previously hidden Pro content will become visible again.</p>
        </div>
      </div>
      
      {subscriptionData?.tier === 'free' && (
        <div className={styles.upgradePrompt}>
          <Crown size={24} className={styles.crownIcon} />
          <h3>Ready to enhance your profile?</h3>
          <p>Upgrade to Pro for just €3/month and unlock all premium features!</p>
          <button className={styles.upgradeButton} onClick={handleUpgrade}>
            <Crown size={16} /> Upgrade Now
          </button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionInfo;